import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';

import {
  MAX_BIO_LENGTH,
  LANGUAGE_LABELS,
  SUPPORTED_LANGUAGES,
  changeHandleRequestSchema,
  updateProfileRequestSchema,
  type ProfileResponse,
} from '@lifetimeline/shared';

import { Button } from '../components/Button.js';
import { Field } from '../components/Field.js';
import { ImagePicker } from '../components/ImagePicker.js';
import { LockIcon } from '../components/icons.js';
import { BackLink } from '../components/BackLink.js';
import { Layout } from '../components/Layout.js';
import { Notice } from '../components/Notice.js';
import { Spinner } from '../components/Spinner.js';
import { useToast } from '../components/Toast.js';
import { readTheme, watchSystemTheme, writeTheme, type ThemeChoice } from '../lib/theme.js';
import { useAuth } from '../features/auth/AuthContext.js';
import { useI18n } from '../i18n/index.js';
import { PROFILE_URL_PREFIX } from '../lib/branding.js';
import { api, errorMessageKey } from '../lib/apiClient.js';
import { outputType } from '../lib/imageFile.js';
import { PROFILE_IMAGE_TICKET, uploadImage } from '../lib/imageUpload.js';
import styles from './pageStyles.module.css';

export function SettingsPage() {
  const { t } = useI18n();
  const { status, profile } = useAuth();

  if (status === 'loading') {
    return (
      <Layout narrow>
        <div className={styles.centered}>
          <Spinner label={t('common.loading')} />
        </div>
      </Layout>
    );
  }
  if (status !== 'authenticated' || profile === null) {
    return <Navigate to="/signin" replace />;
  }

  /*
   * 프로필이 **도착한 뒤에** 폼을 마운트한다.
   *
   * 예전에는 페이지가 직접 `useState(profile?.userName ?? '')`를 들고 있었는데,
   * 초기값은 첫 렌더에 한 번만 평가된다. 그때는 아직 status가 loading이라
   * profile이 null이어서 **이름 칸이 빈 채로 굳었다** — 그대로 저장하면
   * 이름이 지워진다. 자식으로 분리하면 초기값이 진짜 값을 본다.
   */
  return <SettingsForm key={profile.handle} profile={profile} />;
}

/**
 * 주소 바꾸기 — **이름·사진과 따로 저장한다.**
 *
 * 되돌릴 수 없는 결과가 따라온다(지금 주소로 들어오던 링크가 끊긴다). 이름
 * 한 글자 고치는 것과 같은 버튼에 묶으면, 이름만 바꾸려던 사람이 주소까지
 * 바꾸게 된다. 오류도 다르다 — 이미 쓰는 주소인지, 못 쓰는 주소인지.
 */
/**
 * 계정 잠그기.
 *
 * **저장 버튼이 없다.** 켜고 끄는 즉시 반영한다 — 스위치를 올려 두고
 * 저장을 안 눌러서 잠긴 줄 알았는데 안 잠긴 상태가 제일 나쁘다.
 */
function PrivacyToggle({ profile }: { profile: ProfileResponse }) {
  const { t } = useI18n();
  const { setProfile } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    const next = !profile.isPrivate;
    setBusy(true);
    try {
      const updated = await api.patch<ProfileResponse>('/api/me', {
        userName: profile.userName,
        isPrivate: next,
      });
      setProfile(updated);
      toast.show(t(next ? 'settings.privateOn' : 'settings.privateOff'));
    } catch (cause) {
      toast.show(t(errorMessageKey(cause)), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={styles.switchRow}
        role="switch"
        aria-checked={profile.isPrivate}
        disabled={busy}
        onClick={() => void toggle()}
      >
        <span className={styles.switchLabel}>
          <LockIcon size={16} />
          {t('settings.privateLabel')}
        </span>
        <span className={styles.switchTrack} data-on={profile.isPrivate}>
          <span className={styles.switchKnob} />
        </span>
      </button>
      <p className={styles.switchHelp}>{t('settings.privateHelp')}</p>
    </>
  );
}

function AddressForm({ profile }: { profile: ProfileResponse }) {
  const { t } = useI18n();
  const { setProfile } = useAuth();
  const toast = useToast();

  const [handle, setHandle] = useState(profile.handle);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const next = handle.trim();
  const changed = next !== '' && next !== profile.handle;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const parsed = changeHandleRequestSchema.safeParse({ handle: next });
    if (!parsed.success) {
      // 형식은 서버도 보지만, 여기서 걸러야 왜 안 되는지 그 자리에서 보인다
      setError(t('signUp.userIdInvalid'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = await api.patch<ProfileResponse>('/api/me/handle', parsed.data);
      setProfile(updated);
      setHandle(updated.handle);
      toast.show(t('settings.addressSaved'));
    } catch (cause) {
      setError(t(errorMessageKey(cause)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <Field
        label={t('settings.addressFieldLabel')}
        prefix={PROFILE_URL_PREFIX}
        value={handle}
        maxLength={20}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        help={t('settings.addressHelp')}
        error={error}
        onChange={(event) => setHandle(event.target.value)}
      />
      {/*
        경고는 **바꾸려 할 때만** 띄운다. 가만히 있는 사람에게 미리 겁을 줄
        이유가 없고, 늘 떠 있으면 정작 바꿀 때 안 읽는다.
      */}
      {changed ? <p className={styles.addressWarning}>{t('settings.addressWarning')}</p> : null}
      <Button type="submit" loading={saving} disabled={!changed}>
        {t('settings.addressSave')}
      </Button>
    </form>
  );
}

function SettingsForm({ profile }: { profile: ProfileResponse }) {
  const { t, language, setLanguage } = useI18n();
  const { setProfile, signOut } = useAuth();
  const toast = useToast();

  const [userName, setUserName] = useState(profile.userName);
  const [bio, setBio] = useState(profile.bio ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * 고른 사진은 **저장을 누를 때** 올린다.
   *
   * 고르자마자 올리면 저장하지 않고 나간 사람의 파일이 버킷에 그대로 남는다.
   * 지금은 저장까지 간 것만 올라가고, 올린 뒤 PATCH가 실패하면 파일 하나가
   * 남을 뿐 프로필은 멀쩡하다.
   */
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  /** 원래 있던 사진을 지웠는가. "안 골랐다"(`imageBlob === null`)와 다른 상태다 */
  const [imageCleared, setImageCleared] = useState(false);

  const [localPreview, setLocalPreview] = useState<string | null>(null);
  useEffect(() => {
    if (imageBlob === null) {
      setLocalPreview(null);
      return;
    }
    const url = URL.createObjectURL(imageBlob);
    setLocalPreview(url);
    // 안 풀면 고를 때마다 Blob이 메모리에 쌓인다
    return () => URL.revokeObjectURL(url);
  }, [imageBlob]);

  const currentImageUrl = profile.imageUrl ?? null;
  const previewUrl = localPreview ?? (imageCleared ? null : currentImageUrl);
  const imageChanged = imageBlob !== null || (imageCleared && currentImageUrl !== null);
  const nameChanged = userName.trim() !== '' && userName.trim() !== profile.userName;
  const bioChanged = bio.trim() !== (profile.bio ?? '');

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      /*
       * 사진은 안 건드렸으면 **아예 안 보낸다.** 빈 문자열을 보내면 서버는
       * "지워 달라"로 읽으므로, 이름만 바꾸려다 사진이 날아간다.
       */
      let imageUrl: string | undefined;
      if (imageBlob !== null) {
        imageUrl = await uploadImage(imageBlob, outputType(), PROFILE_IMAGE_TICKET);
      } else if (imageCleared && currentImageUrl !== null) {
        imageUrl = '';
      }

      const parsed = updateProfileRequestSchema.safeParse({
        userName: userName.trim(),
        ...(imageUrl === undefined ? {} : { imageUrl }),
        // 안 건드렸으면 아예 안 보낸다 — 빈 문자열은 "지워 달라"는 뜻이다
        ...(bioChanged ? { bio: bio.trim() } : {}),
      });
      if (!parsed.success) {
        setError(t('error.validation'));
        return;
      }

      const updated = await api.patch<ProfileResponse>('/api/me', parsed.data);
      setProfile(updated);
      setImageBlob(null);
      setImageCleared(false);
      toast.show(t('settings.saved'));
    } catch (cause) {
      setError(t(errorMessageKey(cause)));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void save();
  };

  return (
    <Layout narrow>
      <div className={styles.head}>
        <BackLink />
        <h1 className={styles.title}>{t('settings.title')}</h1>
      </div>

      {error !== null ? (
        <div className={styles.noticeSlot}>
          <Notice tone="error">{error}</Notice>
        </div>
      ) : null}

      <section>
        <h2 className={styles.sectionTitle}>{t('settings.profileSection')}</h2>
        <form onSubmit={handleSubmit}>
          <ImagePicker
            label={t('settings.photoLabel')}
            shape="circle"
            previewUrl={previewUrl}
            disabled={saving}
            onPick={(blob) => {
              setImageBlob(blob);
              setImageCleared(false);
            }}
            onClear={() => {
              setImageBlob(null);
              setImageCleared(true);
            }}
          />

          <Field
            label={t('settings.userNameLabel')}
            value={userName}
            maxLength={30}
            onChange={(event) => setUserName(event.target.value)}
          />

          {/*
            한마디는 **길이를 입력칸이 막는다**(`maxLength`). 넘치게 적고 나서
            저장할 때 거절당하면 무엇을 지워야 할지 다시 세야 한다.
          */}
          <Field
            label={t('settings.bioLabel')}
            value={bio}
            maxLength={MAX_BIO_LENGTH}
            onChange={(event) => setBio(event.target.value)}
          />
          <Button
            type="submit"
            loading={saving}
            disabled={userName.trim() === '' || (!nameChanged && !imageChanged && !bioChanged)}
          >
            {t('common.save')}
          </Button>
        </form>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.languageSection')}</h2>
        {/*
          계정에 저장하지 않는다 — 브라우저에만 남는다.
          화면은 언제나 **보는 사람의** 언어로 그려지므로(카테고리 이름은
          클라이언트가 자기 언어 파일로 해석한다) 계정에 적어둘 이유가 없다.
        */}
        <div className={styles.choiceRow}>
          {SUPPORTED_LANGUAGES.map((code) => (
            <Button
              key={code}
              variant={code === language ? 'solid' : 'outline'}
              onClick={() => setLanguage(code)}
            >
              {LANGUAGE_LABELS[code]}
            </Button>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.themeSection')}</h2>
        {/*
          언어와 마찬가지로 **계정이 아니라 브라우저**에 남는다. 테마는 지금
          보는 기기의 사정이다 — 밤에 폰에서 어둡게 보는 사람이 낮에
          노트북에서도 어두울 이유가 없다.
        */}
        <ThemePicker />
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.privateSection')}</h2>
        <PrivacyToggle profile={profile} />
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.addressSection')}</h2>
        <AddressForm profile={profile} />
      </section>

      {/* 헤더가 좁아 로그아웃을 여기 둔다 — 모바일에서 흔한 자리다 */}
      <section className={styles.section}>
        <Button variant="outline" fullWidth onClick={signOut}>
          {t('nav.signOut')}
        </Button>
      </section>
    </Layout>
  );
}

/**
 * 테마 고르기.
 *
 * `시스템`이 기본이다 — 지금까지 앱이 하던 동작이라, 그 동작에 이름을 줘야
 * 직접 고른 사람이 **원래대로 돌아갈 길**이 남는다.
 */
function ThemePicker() {
  const { t } = useI18n();
  /* 첫 그림부터 고른 값으로 — 뒤늦게 읽으면 버튼이 한 번 바뀐다 */
  const [choice, setChoice] = useState<ThemeChoice>(() => readTheme());

  /* 기기가 밤에 저절로 어두워지는 경우를 따라간다(`시스템`일 때만) */
  useEffect(() => watchSystemTheme(() => choice), [choice]);

  const options: ThemeChoice[] = ['system', 'light', 'dark'];
  return (
    <div className={styles.choiceRow}>
      {options.map((option) => (
        <Button
          key={option}
          variant={option === choice ? 'solid' : 'outline'}
          onClick={() => {
            setChoice(option);
            writeTheme(option);
          }}
        >
          {t(`settings.theme.${option}`)}
        </Button>
      ))}
    </div>
  );
}
